const React = require("react");
const T = require("prop-types");
const { default: Dialog } = require("material-ui/Dialog");
const { default: RaisedButton } = require("material-ui/RaisedButton");
const { default: FlatButton } = require("material-ui/FlatButton");
const { RadioButton, RadioButtonGroup } = require("material-ui/RadioButton");
const CircularProgress = require("material-ui/CircularProgress").default;
const CheckoutFormContainer = require("containers/CheckoutForm").default;
// TODO Componentize other dialog steps, make this file less than 700 lines at least please...ffs
const PaymentOptions = require("./PaymentOptions");
const Classes = require("../styles.scss");
const KeyMirror = require("keymirror");
const ContentHelpers = require("utils/content-helpers");

const internals = {};

internals.transactionSteps = KeyMirror({
    SELECT_DOWNLOAD_PACKAGE: true,
    REDEEM_CREDITS: true,
    SELECT_PAYMENT_OPTION: true,
    STRIPE_FORM: true,
    RECEIPT: true
});

module.exports = class TransactionDialog extends React.PureComponent {
    static propTypes = {
        // prop requirements kept very loose since different paths through
        // dialog have different dependencies. unknown which steps a user's transaction
        // will go through, which props their path will require
        dialogOpen: T.bool.isRequired,
        displayError: T.func.isRequired,
        downloadPackages: T.objectOf(
            T.shape({
                cost: T.number.isRequired,
                id: T.number.isRequired,
                name: T.string.isRequired
            })
        ),
        numCredits: T.number.isRequired,
        onCheckoutFormSubmit: T.func,
        onDownloadPackageSelected: T.func,
        onPaymentOptionSelected: T.func,
        onReceiptSuccessButtonClick: T.func,
        onRedeemFormSubmit: T.func,
        onRequestDialogClose: T.func.isRequired,
        packageNames: T.objectOf(T.number).isRequired, // Map of package names to ids
        selectedPaymentOptionId: T.number,
        selectedDownloadPackageId: T.number,
        paymentOptions: T.arrayOf(
            T.shape({
                id: T.number.isRequired,
                cost: T.number.isRequired,
                numCredits: T.number.isRequired,
                description: T.string.isRequired
            })
        ),
        receipt: T.shape({
            cost: T.number.isRequired,
            paymentOption: T.shape({
                numCredits: T.number.isRequired
            }).isRequired,
            promo: T.shape({
                code: T.string.isRequired,
                description: T.string.isRequired
            })
        }),
        transaction: T.shape({
            type: T.oneOf(["credits", "purchase", "upgrade"]).isRequired,
            id: T.number // Not required; exists only if a purchase or upgrade (model transaction)
        }).isRequired
    };

    constructor(props, context) {
        super(props, context);

        this.advance = this._advance.bind(this);
        this.back = this._back.bind(this);
        this.checkoutFormSubmit = this._checkoutFormSubmit.bind(this);
        this.configureStep = this._configureStep.bind(this);
        this.determineNextIncompleteStep = this._determineNextIncompleteStep.bind(
            this
        );
        this.downloadPackageChanged = this._downloadPackageChanged.bind(this);
        this.firstStep = this._firstStep.bind(this);
        this.paymentOptionChanged = this._paymentOptionChanged.bind(this);
        this.renderActions = this._renderActions.bind(this);
        this.renderDownloadPackages = this._renderDownloadPackages.bind(this);
        this.renderPaymentOptions = this._renderPaymentOptions.bind(this);
        this.renderReceipt = this._renderReceipt.bind(this);
        this.renderRedeem = this._renderRedeem.bind(this);
        this.renderStripeForm = this._renderStripeForm.bind(this);
        this.setDisabled = this._setDisabled.bind(this);
        this.setLoading = this._setLoading.bind(this);
        this.submitDownloadPackage = this._submitDownloadPackage.bind(this);
        this.submitPaymentOption = this._submitPaymentOption.bind(this);
        this.submitRedeem = this._submitRedeem.bind(this);
        this.toggleDisabled = this._toggleDisabled.bind(this);
        this.toggleLoading = this._toggleLoading.bind(this);

        this.state = {
            proposedDownloadPackageId: null,
            proposedPaymentOptionId: null,
            isDisabled: false,
            isLoading: false,
            transactionComplete: false,
            completedSteps: [],
            currentStep: null
        };

        // Calculate step only to track in history e.g. when user starts a transaction or advances a step
        // For rendering purposes, read calculation stored in component state. If nothing else, doing so
        // allows us to navigate back in the dialog regardless of our props / state, which dictate which step
        // the component believes we should be at based on determineNextIncompleteStep
        if (props.transaction) {
            const firstStep = this.determineNextIncompleteStep();
            this.state.currentStep = firstStep;
        }
    }

    _setDisabled(val) {
        this.setState({ isDisabled: val });
    }

    _setLoading(val) {
        this.setState({ isLoading: val });
    }

    _toggleLoading() {
        this.setState({ isLoading: !this.state.isLoading });
    }

    _toggleDisabled() {
        this.setState({ isDisabled: !this.state.isDisabled });
    }

    /**
     * determineNextIncompleteStep is, effectively, our dialog router, deriving the latest step at which
     * the user can progress based on relevant state (transaction-related props and form (component) state)
     *
     * The result of determineNextIncompleteStep always points to the latest POSSIBLE step for which the user hasn't satisfied our prerequisites —
     * an incomplete step — based on state.
     *
     * See advance for the crux of how we use this logic
     *
     */
    _determineNextIncompleteStep() {
        const {
            downloadPackages,
            numCredits,
            selectedDownloadPackageId,
            selectedPaymentOptionId,
            transaction
        } = this.props;
        const { transactionComplete } = this.state;

        // What are you transacting?
        switch (transaction.type) {
            // Buying credits
            case "credits":
                // Which payment option?
                if (selectedPaymentOptionId) {
                    if (transactionComplete) {
                        return internals.transactionSteps.RECEIPT;
                    }
                    return internals.transactionSteps.STRIPE_FORM;
                }
                return internals.transactionSteps.SELECT_PAYMENT_OPTION;

            // Model transactions
            case "upgrade":
                // Which payment option?
                if (selectedPaymentOptionId) {
                    if (transactionComplete) {
                        return internals.transactionSteps.RECEIPT;
                    }
                    return internals.transactionSteps.STRIPE_FORM;
                }

                // sufficient funds?
                // This must come after checking for a selected payment option,
                // so, the dialog path of purchasing credits, if you were to purchase more credits and make the below condition true,
                // you'd still progress to the RECEIPT step, as your component state would have a selectedPaymentOption,
                // rather than the REDEEM_CREDITS step, which wouldn't make sense give that the STRIPE_FORM is expected to expose
                // a similar transaction-completing action as REDEEM_CREDITS
                if (numCredits >= 1) {
                    return internals.transactionSteps.REDEEM_CREDITS;
                }

                return internals.transactionSteps.SELECT_PAYMENT_OPTION;

            case "purchase":
                // Which download package would you like to purchase?
                if (selectedDownloadPackageId) {
                    // Which payment option?
                    if (selectedPaymentOptionId) {
                        if (transactionComplete) {
                            return internals.transactionSteps.RECEIPT;
                        }
                        return internals.transactionSteps.STRIPE_FORM;
                    }

                    // sufficient funds?
                    if (
                        numCredits >=
                        downloadPackages[selectedDownloadPackageId].cost
                    ) {
                        return internals.transactionSteps.REDEEM_CREDITS;
                    }

                    return internals.transactionSteps.SELECT_PAYMENT_OPTION;
                }
                return internals.transactionSteps.SELECT_DOWNLOAD_PACKAGE;
        }
    }

    _configureStep(step) {
        // TODO Need a default step?
        switch (step) {
            case internals.transactionSteps.SELECT_DOWNLOAD_PACKAGE:
                return this.renderDownloadPackages();

            case internals.transactionSteps.REDEEM_CREDITS:
                return this.renderRedeem();

            case internals.transactionSteps.SELECT_PAYMENT_OPTION:
                return this.renderPaymentOptions();

            case internals.transactionSteps.STRIPE_FORM:
                return this.renderStripeForm();

            case internals.transactionSteps.RECEIPT:
                return this.renderReceipt();
        }
    }

    _back() {
        if (this.firstStep()) {
            return null;
        }

        this.setState(prevState => {
            const { completedSteps, currentStep } = prevState;

            // if we're on an incomplete step, go back to last known complete step
            // if we're on a completed step, go back to previous complete step
            const incompleteStep = completedSteps.indexOf(currentStep) === -1;
            const targetStep = incompleteStep
                ? completedSteps[completedSteps.length - 1]
                : completedSteps[completedSteps.indexOf(currentStep) - 1];

            return {
                currentStep: targetStep,
                // Clear any and all proposed keys on moving step
                // that state is step-specific, so changing step
                // always means that any step-specific entries are
                // no longer relevant, fine to null
                proposedDownloadPackageId: null,
                proposedPaymentOptionId: null
            };
        });
    }

    _firstStep() {
        const { completedSteps, currentStep } = this.state;
        return completedSteps.length === 0 || completedSteps[0] === currentStep;
    }

    /**
     * Handler for progressing on from any given step. This is the core logic for managing the user's progress within the dialog
     *
     * 1. Fires the given submitAction to trigger arbitrary component / application logic e.g. Redux actions
     * 2. Decides how to treat this step movement. There are 3 possibilities:
     *      - User is moving from one previously completed step to another: Walk forward one index in our completedSteps sequence
     *      - User is moving from a previously completed step to an incomplete one: Transition currentStep to the result of determineNextIncompleteStep
     *      - User is newly completing a step, moving onto a previously unseen one: Transition currentStep to the result of determineNextIncompleteStep
     *      and transitioning our currentStep to completed (commit the result)
     *
     * TLDR New commit if making new history; walk existing commits if moving back through time
     */
    _advance(submitAction) {
        return () => {
            // async to ensure all props/state reflects all work in submitAction
            // before running determineNextIncompleteStep
            return Promise.resolve()
                .then(() => {
                    return submitAction();
                })
                .then(() => {
                    // Refresh step state based on changes to state
                    // since last step e.g. selecting a payment option
                    const step = this.determineNextIncompleteStep();
                    this.setState(prevState => {
                        const { completedSteps, currentStep } = prevState;

                        // We've tracked the next incomplete step in our completedSteps
                        // OR we determined that the step we're completing currently (either an incomplete step moving to complete or a previously completed step)
                        // is actually incomplete. Both cases are non-sensical, indicative of our mistake, so blow up
                        if (
                            completedSteps.indexOf(step) !== -1 ||
                            step === currentStep
                        ) {
                            // TODO Is throwing the right thing to do? Crashes the entire React application (whitescreen in browser)
                            throw new Error("MISTRACKED PROGRESS!");
                        }

                        // If we're reviewing a previously completed step that's NOT the last step
                        // we completed, just move forward to the next step in our history instead of appending
                        // and navigating to what should be the furthest step in our progress
                        if (completedSteps.indexOf(currentStep) !== -1) {
                            // If we're at the last completed step, move to the next incomplete step (as calculated by determineNextIncompleteStep)
                            // WITHOUT mutating our completedSteps, as we're not completing a new step here, but completing a review of a previously completed one
                            if (
                                completedSteps.indexOf(currentStep) ===
                                completedSteps.length - 1
                            ) {
                                return {
                                    currentStep: step
                                };
                            }

                            return {
                                currentStep:
                                    completedSteps[
                                        completedSteps.indexOf(currentStep) + 1
                                    ]
                            };
                        }

                        // Completing a previously incomplete step
                        return {
                            completedSteps: [
                                ...prevState.completedSteps,
                                currentStep
                            ],
                            currentStep: step
                        };
                    });
                });
        };
    }

    _renderActions(submitLabel, submitAction, progressDisabled) {
        const {
            onReceiptSuccessButtonClick,
            onRequestDialogClose,
            transaction
        } = this.props;
        const { currentStep, isDisabled } = this.state;
        const isModelTransaction = ["purchase", "upgrade"].includes(
            transaction.type
        );

        if (currentStep === internals.transactionSteps.RECEIPT) {
            return (
                <div>
                    {isModelTransaction ? (
                        <RaisedButton
                            primary
                            label="Go to Model"
                            className={`${Classes.modalButton} ${Classes.modalButtonSubmit}`}
                            onClick={onReceiptSuccessButtonClick}
                        />
                    ) : (
                        <RaisedButton
                            label="Done"
                            onClick={onRequestDialogClose}
                            className={Classes.modalButton}
                            secondary
                        />
                    )}
                </div>
            );
        }
        return (
            <div>
                <FlatButton
                    label="Cancel"
                    className={Classes.modalButton}
                    onClick={onRequestDialogClose}
                />
                <RaisedButton
                    disabled={this.firstStep()}
                    label="Back"
                    className={Classes.modalButton}
                    onClick={this.back}
                    secondary
                />
                {submitLabel && submitAction && (
                    <RaisedButton
                        disabled={progressDisabled || isDisabled}
                        primary
                        label={submitLabel}
                        onClick={this.advance(submitAction)}
                        className={`${Classes.modalButton} ${Classes.modalButtonSubmit} select_btn`}
                    />
                )}
            </div>
        );
    }

    _renderDownloadPackages() {
        const {
            downloadPackages,
            selectedDownloadPackageId,
            transaction
        } = this.props;
        const { proposedDownloadPackageId } = this.state;
        const actions = this.renderActions(
            "Next",
            this.submitDownloadPackage,
            !(selectedDownloadPackageId || proposedDownloadPackageId)
        );

        const isModelTransaction = ["purchase", "upgrade"].includes(
            transaction.type
        );

        if (downloadPackages === null) {
            return {
                dialogAttrs: {
                    title: "Checkout",
                    actions
                },
                view: <div>Loading...</div>
            };
        }

        return {
            dialogAttrs: {
                title: !isModelTransaction
                    ? "Choose Your Credit Pack"
                    : "Select a Download Package",
                actions
            },
            view: (
                <div>
                    <h4>
                        Which download package would you like for your model?
                    </h4>
                    <RadioButtonGroup
                        key="downloadPackagesButtonGroup" // key needed to ensure other steps e.g. paymentOptions that use this component don't accidentally reuse it
                        name="downloadPackagesButtonGroup"
                        defaultSelected={selectedDownloadPackageId}
                        onChange={this.downloadPackageChanged}
                        className={Classes.optionsGroup}
                    >
                        {Object.keys(downloadPackages).map(pkgId => {
                            // z-index on labels ensures that links rise above input elements, so they're clickable (instead of the input elements
                            // covering the labels, thereby capturing the click events)
                            const pkg = downloadPackages[pkgId];
                            return (
                                <RadioButton
                                    key={`downloadPackageButton_${pkg.id}`}
                                    value={pkg.id}
                                    label={
                                        <span>
                                            {ContentHelpers.capitalize(
                                                pkg.name
                                            )}{" "}
                                            Download Package — {pkg.cost} Credit
                                            {ContentHelpers.plural(pkg.cost)}.
                                        </span>
                                    }
                                    labelStyle={{ zIndex: "1000" }}
                                />
                            );
                        })}
                    </RadioButtonGroup>
                </div>
            )
        };
    }

    _downloadPackageChanged(ev, value) {
        this.setState({
            proposedDownloadPackageId: value
        });
    }

    _submitDownloadPackage() {
        const { selectedDownloadPackageId } = this.props;
        const { proposedDownloadPackageId } = this.state;

        if (!proposedDownloadPackageId) {
            // No-op if no proposed value, but one already set in state up the tree
            if (selectedDownloadPackageId) {
                return;
            }

            // Error if no possible value submitted for download package
            this.props.displayError("Please select a download package");
            return;
        }

        this.props.onDownloadPackageSelected(proposedDownloadPackageId);
        this.setState({
            // Reset our progress; consider switching download packages as
            // restarting the dialog, requiring a new payment selection relative
            // to the cost of the selected package
            completedSteps: [
                internals.transactionSteps.SELECT_DOWNLOAD_PACKAGE
            ],
            proposedDownloadPackageId: null
        });
    }

    _renderRedeem() {
        const {
            downloadPackages,
            numCredits,
            onRequestDialogClose,
            selectedDownloadPackageId,
            transaction
        } = this.props;
        const { isLoading } = this.state;

        const redeemContent = transactionType => {
            switch (transactionType) {
                case "purchase":
                    const { name, cost } = downloadPackages[
                        selectedDownloadPackageId
                    ];
                    return (
                        <div>
                            <h4>
                                Use {cost} Credit{ContentHelpers.plural(cost)}{" "}
                                to create a {ContentHelpers.capitalize(name)}{" "}
                                download package of this body model?
                            </h4>
                            <br />
                            <p>
                                Please note that you cannot edit your download
                                package after purchasing for download. Includes
                                our{" "}
                                <a
                                    href="https://mybodymodel.com/termsofuse"
                                    target="_blank"
                                >
                                    Standard License.
                                </a>
                            </p>
                        </div>
                    );
                case "upgrade":
                    return (
                        <div>
                            {/* TODO Undo this hardcoding, key in to packages upgrade node? */}
                            <h4>
                                Use 1 Credit to upgrade your Basic Purchase to a
                                Premium Purchase?
                            </h4>
                            <br />
                            <p>
                                Please note that you cannot edit your download
                                package after purchasing for download. Includes
                                our{" "}
                                <a
                                    href="https://mybodymodel.com/termsofuse"
                                    target="_blank"
                                >
                                    Standard License.
                                </a>
                            </p>
                        </div>
                    );
            }
        };

        return {
            dialogAttrs: {
                title: `You Have ${numCredits} Credit${
                    numCredits > 1 ? "s" : ""
                }`,
                actions: [
                    <div>
                        <FlatButton
                            label="Cancel"
                            onClick={onRequestDialogClose}
                            className={Classes.modalButton}
                        />
                        <RaisedButton
                            disabled={this.firstStep()}
                            label="Back"
                            className={Classes.modalButton}
                            onClick={this.back}
                            secondary
                        />
                        <RaisedButton
                            primary
                            label="Use Credit"
                            onClick={this.submitRedeem}
                            className={`${Classes.modalButton} ${Classes.modalButtonSubmit}`}
                        />
                    </div>
                ]
            },
            view: (
                <div>
                    {redeemContent(transaction.type)}
                    {isLoading && (
                        <CircularProgress
                            className={Classes.loader}
                            size={20}
                        />
                    )}
                </div>
            )
        };
    }

    _submitRedeem() {
        let errMessage;

        this.toggleLoading();

        return this.props.onRedeemFormSubmit().catch(({ response }) => {
            if (response && response.data) {
                errMessage = response.data.message;
            }

            this.toggleLoading();
            this.props.displayError(
                errMessage || "Error redeeming Credits. Please try again"
            );
        });
    }

    _renderPaymentOptions() {
        const {
            downloadPackages,
            numCredits,
            packageNames,
            paymentOptions,
            selectedDownloadPackageId,
            selectedPaymentOptionId,
            transaction
        } = this.props;
        const { proposedPaymentOptionId } = this.state;
        const actions = this.renderActions(
            "Next",
            this.submitPaymentOption,
            !(selectedPaymentOptionId || proposedPaymentOptionId)
        );

        if (paymentOptions === null) {
            return {
                dialogAttrs: {
                    title: "Checkout",
                    actions
                },
                view: <div>Loading...</div>
            };
        }

        // TODO Might need to keep this one hardcoded for now...requirement still tenuous
        const onPremiumClick = () => {
            const paymentOptStep =
                internals.transactionSteps.SELECT_PAYMENT_OPTION;

            this.setState(({ completedSteps, currentStep }) => {
                const stepIndex = completedSteps.indexOf(paymentOptStep);

                // If we haven't registered the step we're on as the current step OR
                // we previously marked the step as complete but it isn't the last completed step (at the time of writing,
                // this must always be true, as the step that follows, the checkout form, is complete only when we've finished
                // the transaction i.e. can no longer come back to this step), then we've screwed something up
                if (
                    currentStep !== paymentOptStep ||
                    (stepIndex !== -1 &&
                        stepIndex !== completedSteps.length - 1)
                ) {
                    throw new Error("Mistracked progress!");
                }

                return {
                    proposedPaymentOptionId: null,
                    // If the step had been marked as completed
                    // Incomplete it; we're treating switching to Premium as exceptional, resetting
                    // the flow through the dialog
                    completedSteps:
                        stepIndex !== -1
                            ? completedSteps.slice(0, -1)
                            : completedSteps
                };
            });
            this.props.onDownloadPackageSelected(packageNames.premium);
        };

        return {
            dialogAttrs: {
                title: this.isModelTransaction
                    ? "Add Credits"
                    : "Choose Your Credit Pack",
                actions
            },
            view: (
                <PaymentOptions
                    key={`PaymentOptions_pkg${selectedDownloadPackageId}`}
                    currentPaymentOption={
                        proposedPaymentOptionId || selectedPaymentOptionId
                    }
                    downloadPackage={
                        downloadPackages[selectedDownloadPackageId]
                    }
                    numCredits={numCredits}
                    onPremiumClick={onPremiumClick}
                    paymentOptionChanged={this.paymentOptionChanged}
                    paymentOptions={paymentOptions}
                    transaction={transaction}
                />
            )
        };
    }

    _paymentOptionChanged(ev, value) {
        this.setState({
            proposedPaymentOptionId: value
        });
    }

    _submitPaymentOption() {
        const { selectedPaymentOptionId } = this.props;
        const { proposedPaymentOptionId } = this.state;

        if (!proposedPaymentOptionId) {
            // No-op if no proposed value, but one already set in state up the tree
            if (selectedPaymentOptionId) {
                return;
            }

            // Error if no possible value submitted for payment option
            this.props.displayError("Please select a payment option");
            return;
        }

        this.setState({
            proposedPaymentOptionId: null
        });
        this.props.onPaymentOptionSelected(proposedPaymentOptionId);
    }

    _renderStripeForm() {
        const {
            displayError,
            paymentOptions,
            selectedPaymentOptionId
        } = this.props;
        const { isLoading, isDisabled } = this.state;
        const purchaseOption = paymentOptions.find(
            opt => opt.id === selectedPaymentOptionId
        );

        return {
            dialogAttrs: {
                title:
                    purchaseOption === null
                        ? "No option selected"
                        : purchaseOption.description,
                actions: this.renderActions()
            },
            view: (
                <div>
                    <CheckoutFormContainer
                        onSubmit={this.checkoutFormSubmit}
                        onError={displayError}
                        purchaseOption={purchaseOption}
                        disableSubmit={isDisabled}
                    />
                    {isLoading && (
                        <CircularProgress
                            className={Classes.loader}
                            size={20}
                        />
                    )}
                </div>
            )
        };
    }

    _checkoutFormSubmit(stripe, elements, promoCode) {
        this.setDisabled(true);
        this.setLoading(true);

        elements.submit().then(({ error: submitError }) => {
            if (submitError) {
                this.props.displayError(submitError.message);
                this.setDisabled(false);
                this.setLoading(false);
                return;
            }

            stripe
                .createConfirmationToken({ elements })
                .then(({ error: confirmError, confirmationToken }) => {
                    if (confirmError) {
                        this.props.displayError(confirmError.message);
                        this.setDisabled(false);
                        this.setLoading(false);
                        return;
                    }

                    return this.props.onCheckoutFormSubmit(
                        confirmationToken,
                        promoCode
                    );
                })
                .then(() => {
                    // Separate state setting, as for our step calculation to work
                    // as expected, we need to set transactionComplete accordingly
                    this.setState(() => ({
                        transactionComplete: true
                    }));
                })
                .then(() => {
                    // Refresh step state based on changes to state
                    // since last step e.g. selecting a payment option
                    const step = this.determineNextIncompleteStep();
                    const { completedSteps, currentStep } = this.state;
                    if (
                        completedSteps.indexOf(step) !== -1 ||
                        step === currentStep
                    ) {
                        const TrackingError = new Error("MISTRACKED PROGRESS!");
                        TrackingError.name = "StepTrackingError";
                        throw TrackingError;
                    }

                    return this.setState(prevState => {
                        return {
                            // We don't care about or try to handle any cases of navigating back to this page,
                            // shouldn't be possible to navigate back to and resubmit this step (enforce in receipt page logic)
                            completedSteps: [
                                ...prevState.completedSteps,
                                prevState.currentStep
                            ],
                            currentStep: step
                        };
                    });
                })
                .catch(err => {
                    let errMessage;

                    // API error
                    if (err.response && err.response.data) {
                        errMessage = err.response.data.message;
                    }

                    if (err.name === "StepTrackingError") {
                        console.error(
                            "Programmer error in step state mgmt",
                            err
                        );
                        errMessage = `Purchase completed successfully! If you weren\'t redirected to the receipt page of your popup, please
                    inform the site administrator (help@mybodymodel) as this indiciates a maintenance issue. Your purchase was still successful
                    and any Credits purchased should now appear in your balance. Thanks!`;
                    }

                    this.toggleLoading();
                    this.toggleDisabled();

                    this.props.displayError(
                        errMessage ||
                            "Error completing purchase. Please try again"
                    );
                });
        });
    }

    _renderReceipt() {
        const {
            downloadPackages,
            numCredits,
            receipt,
            selectedDownloadPackageId,
            transaction
        } = this.props;
        const isModelTransaction = ["purchase", "upgrade"].includes(
            transaction.type
        );

        let name;
        let cost;
        if (isModelTransaction) {
            const pkg = downloadPackages[selectedDownloadPackageId];
            name = pkg.name;
            cost = pkg.cost;
        }

        const deducted = () => {
            if (!isModelTransaction) {
                throw new Error("deducted called for a Credits transaction");
            }

            if (transaction.type === "upgrade") {
                return 1;
            }

            return cost;
        };

        return {
            dialogAttrs: {
                title: "Success",
                actions: this.renderActions()
            },
            view: (
                <div>
                    <p>
                        Thank you for your payment! You will be emailed a
                        receipt.
                    </p>
                    {isModelTransaction ? (
                        <p>
                            {deducted()} Credit
                            {ContentHelpers.plural(deducted())}{" "}
                            {ContentHelpers.plural(deducted()) ? "were" : "was"}{" "}
                            redeemed to create a{" "}
                            {ContentHelpers.capitalize(name)} Download Package
                            for your body model.
                        </p>
                    ) : (
                        <p>
                            {receipt.paymentOption.numCredits} Credit
                            {ContentHelpers.plural(
                                receipt.paymentOption.numCredits
                            )}{" "}
                            {ContentHelpers.plural(
                                receipt.paymentOption.numCredits
                            )
                                ? "have"
                                : "has"}{" "}
                            been added to your account.
                        </p>
                    )}

                    {receipt && receipt.promo && (
                        <p>
                            {receipt.promo.code} Applied!{" "}
                            {receipt.promo.description}
                        </p>
                    )}

                    <p>
                        <strong>Total Cost:</strong> $
                        {(receipt.cost / 100).toFixed(2)}
                    </p>
                    {isModelTransaction && (
                        <p>
                            <strong>Credit Balance:</strong> {numCredits}
                        </p>
                    )}
                </div>
            )
        };
    }

    render() {
        const { dialogOpen, onRequestDialogClose, transaction } = this.props;
        const { currentStep } = this.state;

        if (!transaction) {
            return null;
        }

        const config = this.configureStep(currentStep);

        return (
            <Dialog
                open={dialogOpen}
                modal
                onRequestClose={onRequestDialogClose}
                autoScrollBodyContent
                autoDetectWindowHeight
                paperClassName={Classes.modal}
                overlayClassName={Classes.overlay}
                {...config.dialogAttrs} // TODO Don't spread, just set?
            >
                {config.view}
            </Dialog>
        );
    }
};



// WEBPACK FOOTER //
// ./src/components/Dialog/TransactionDialog/index.js